
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/contexts/EntitlementContext';
import { Loader2 } from 'lucide-react';
import { consumeSessionKicked } from '@/lib/sessionKick';


interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  /** If false, allow pending/rejected users (e.g. for the /pending-approval page itself) */
  requireApproved?: boolean;
  /** Restrict to admins and beta_internal members (Beta-only routes). */
  requireBeta?: boolean;
}

const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireApproved = true,
  requireBeta = false,
}: ProtectedRouteProps) => {
  const { user, loading: authLoading, authReady, isAdmin, userRole } = useAuth();
  const { loading: entLoading, approvalStatus, isBeta } = useEntitlements();

  // Block only on auth — entitlements load in the background and don't gate
  // navigation, so route transitions feel instant. Approval is re-checked
  // below once it resolves.
  if (authLoading || !authReady) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Only protected pages redirect. If the session vanished because the account
    // signed in elsewhere, carry the flag through so /auth explains why.
    const kicked = consumeSessionKicked();
    return <Navigate to={kicked ? '/auth?kicked=1' : '/auth'} replace />;
  }


  const effectiveAdmin = isAdmin || userRole === 'admin';

  if (requireAdmin && !effectiveAdmin) {
    // If entitlements haven't resolved yet, wait — admin flag may still arrive.
    if (entLoading) {
      return (
        <div className="min-h-dvh flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (requireApproved && !effectiveAdmin) {
    // Only rejected accounts are gated now — everyone else gets straight in.
    if (approvalStatus === 'rejected') {
      return <Navigate to="/pending-approval" replace />;
    }
  }


  if (requireBeta && !effectiveAdmin && !isBeta) {
    if (entLoading) {
      return (
        <div className="min-h-dvh flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    return (
      <div className="min-h-dvh flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="inline-block text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/40 mb-3">
            Beta
          </div>
          <h1 className="text-2xl font-bold mb-2">Beta feature</h1>
          <p className="text-muted-foreground">
            This feature is currently in private beta and is only available to admins
            and Beta members. Contact support to join the beta programme.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
